import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { ItemHeader } from './ItemHeader';
import { QuestionRow } from './QuestionRow';
import { AddQuestionRow } from './AddQuestionRow';
import { Button } from '@/components/ui/button';
import { Settings, Trash } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@client/lib';
import { ConditionalBuilder } from './ConditionalBuilder';

interface GroupAccordionProps {
  sectionId: string;
  subsectionId: string;
  group: PolicyFormQuestionGroup;
  questions: Record<string, PolicyFormQuestion>;
  onQuestionGroupChange: (
    sectionId: string,
    subsectionId: string,
    groupId: string,
    updates: Partial<PolicyFormQuestionGroup>,
  ) => void;
  onQuestionChange: (questionId: string, updates: Partial<PolicyFormQuestion>) => void;
  onRemoveQuestion: (
    sectionId: string,
    subsectionId: string,
    groupId: string,
    questionId: string,
  ) => void;
  onAddQuestionToGroup: (
    sectionId: string,
    subsectionId: string,
    groupId: string,
    question: PolicyFormQuestion,
  ) => void;
  onToggleConditional?: () => void;
  onRemoveGroup: (sectionId: string, subsectionId: string, groupId: string) => void;
}

export function GroupAccordion({
  sectionId,
  subsectionId,
  group,
  onRemoveGroup,
  ...props
}: GroupAccordionProps) {
  const [isConditionOpen, setIsConditionOpen] = useState(false);

  return (
    <Accordion type="multiple">
      <AccordionItem
        value={group.id}
        className="rounded-md border"
      >
        <AccordionTrigger className="rounded-md bg-gray-50 px-2 py-1 text-gray-900 hover:bg-gray-100">
          <ItemHeader
            title={group.title || ''}
            onTitleChange={t =>
              props.onQuestionGroupChange(sectionId, subsectionId, group.id, { title: t })
            }
            onToggleConditional={props.onToggleConditional}
          />
          <button
            type="button"
            aria-label="Edit conditional logic"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setIsConditionOpen(true);
            }}
          >
            <Settings
              className={cn(
                'h-4 w-4 flex-shrink-0',
                group.conditional ? 'text-green-500' : 'text-gray-700',
              )}
            />
          </button>

          <ConditionalBuilder
            open={isConditionOpen}
            title={`Conditional Logic - Group`}
            visibleCondition={group.conditional?.visible}
            requiredCondition={group.conditional?.required}
            onChangeVisible={c =>
              props.onQuestionGroupChange(sectionId, subsectionId, group.id, {
                conditional: { ...group.conditional, visible: c },
              })
            }
            onChangeRequired={c =>
              props.onQuestionGroupChange(sectionId, subsectionId, group.id, {
                conditional: { ...group.conditional, required: c },
              })
            }
            onClose={() => {
              setIsConditionOpen(false);
            }}
            questions={props.questions}
          />

          <Button
            variant="ghost"
            size="sm"
            aria-label="Remove group"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onRemoveGroup(sectionId, subsectionId, group.id);
            }}
          >
            <Trash
              size={14}
              className="text-red-700"
            />
          </Button>
        </AccordionTrigger>

        <AccordionContent className="space-y-2 px-2 py-2">
          {group.questions.map(qId => {
            const q = props.questions[qId];
            if (!q) return null;
            return (
              <QuestionRow
                key={q.id}
                q={q}
                subsectionId={subsectionId}
                sectionId={sectionId}
                groupId={group.id}
                {...props}
              />
            );
          })}
          <AddQuestionRow
            sectionId={sectionId}
            subsectionId={subsectionId}
            groupId={group.id}
            onAddQuestionToGroup={props.onAddQuestionToGroup}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
